/**
 * Merges persisted chat history with live stream messages for ChatScreen: dedupe, metadata
 * enrichment, display ordering and progress-only placeholder handling.
 */
import type { ChatMessage } from '../types';
import { parseAgentResponse } from './agentResponse';

const STREAM_PROGRESS_PREFIX = '[progress]';

const getAttachmentDedupeSignature = (message: ChatMessage) =>
  (message.attachments || [])
    .map((attachment) => `${attachment.fileName}:${attachment.mimeType}:${attachment.fileSize}`)
    .join('|');

export const getMessageDedupeKey = (message: ChatMessage) =>
  [
    message.role,
    message.content.trim().replace(/\s+/g, ' '),
    getAttachmentDedupeSignature(message),
  ].join('\u0000');

const normalizeContent = (content: string) => content.trim().replace(/\s+/g, ' ');

/** Shorter persisted answers are never matched by suffix: too likely to collide with other turns. */
const MIN_FINAL_ANSWER_MATCH_CHARS = 20;

/**
 * A live agent message accumulates the text of every tool round (e.g. "I will search the web..."
 * followed by the answer), while the persisted copy keeps only the final answer.
 */
const isFinalAnswerOfLiveMessage = (liveMessage: ChatMessage, persistedMessage: ChatMessage) => {
  if (liveMessage.role !== 'agent' || persistedMessage.role !== 'agent') return false;
  const persistedContent = normalizeContent(persistedMessage.content);
  if (persistedContent.length < MIN_FINAL_ANSWER_MATCH_CHARS) return false;
  return normalizeContent(liveMessage.content).endsWith(persistedContent);
};

export const removePersistedMessagesDuplicatedByLiveMessages = (
  persistedMessages: ChatMessage[],
  liveMessages: ChatMessage[]
) => {
  const unmatchedLiveIndexesByKey = new Map<string, number[]>();
  liveMessages.forEach((message, index) => {
    const key = getMessageDedupeKey(message);
    unmatchedLiveIndexesByKey.set(key, [...(unmatchedLiveIndexesByKey.get(key) ?? []), index]);
  });
  const consumedLiveIndexes = new Set<number>();

  const dedupedReversed: ChatMessage[] = [];
  for (let index = persistedMessages.length - 1; index >= 0; index -= 1) {
    const message = persistedMessages[index];
    const exactLiveIndex = unmatchedLiveIndexesByKey
      .get(getMessageDedupeKey(message))
      ?.find((liveIndex) => !consumedLiveIndexes.has(liveIndex));

    if (exactLiveIndex !== undefined) {
      consumedLiveIndexes.add(exactLiveIndex);
      continue;
    }

    const finalAnswerLiveIndex = liveMessages.findIndex(
      (liveMessage, liveIndex) =>
        !consumedLiveIndexes.has(liveIndex) && isFinalAnswerOfLiveMessage(liveMessage, message)
    );
    if (finalAnswerLiveIndex >= 0) {
      consumedLiveIndexes.add(finalAnswerLiveIndex);
      continue;
    }

    dedupedReversed.push(message);
  }

  return dedupedReversed.reverse();
};

const takeFinalAnswerPersistedMessage = (
  persistedMessagesByKey: Map<string, ChatMessage[]>,
  liveMessage: ChatMessage
) => {
  for (const messagesForKey of persistedMessagesByKey.values()) {
    const matchIndex = messagesForKey.findIndex((message) =>
      isFinalAnswerOfLiveMessage(liveMessage, message)
    );
    if (matchIndex >= 0) return messagesForKey.splice(matchIndex, 1)[0];
  }
  return undefined;
};

export const enrichLiveMessagesWithPersistedMetadata = (
  persistedMessages: ChatMessage[],
  liveMessages: ChatMessage[]
) => {
  if (persistedMessages.length === 0 || liveMessages.length === 0) {
    return liveMessages;
  }

  const persistedMessagesByKey = new Map<string, ChatMessage[]>();
  for (let index = persistedMessages.length - 1; index >= 0; index -= 1) {
    const message = persistedMessages[index];
    const key = getMessageDedupeKey(message);
    const messagesForKey = persistedMessagesByKey.get(key) ?? [];
    messagesForKey.push(message);
    persistedMessagesByKey.set(key, messagesForKey);
  }

  const enrichedReversed: ChatMessage[] = [];
  for (let index = liveMessages.length - 1; index >= 0; index -= 1) {
    const liveMessage = liveMessages[index];
    const key = getMessageDedupeKey(liveMessage);
    const persistedMessage =
      persistedMessagesByKey.get(key)?.shift() ??
      takeFinalAnswerPersistedMessage(persistedMessagesByKey, liveMessage);

    if (!persistedMessage) {
      enrichedReversed.push(liveMessage);
      continue;
    }

    enrichedReversed.push({
      ...liveMessage,
      reasoning: liveMessage.reasoning?.trim() ? liveMessage.reasoning : persistedMessage.reasoning,
      sources:
        liveMessage.sources && liveMessage.sources.length > 0
          ? liveMessage.sources
          : persistedMessage.sources,
      attachments:
        persistedMessage.attachments && persistedMessage.attachments.length > 0
          ? persistedMessage.attachments
          : liveMessage.attachments,
      diagrams:
        liveMessage.diagrams && liveMessage.diagrams.length > 0
          ? liveMessage.diagrams
          : persistedMessage.diagrams,
      confirmation: (() => {
        const liveConf = liveMessage.confirmation;
        const persistedConf = persistedMessage.confirmation;
        if (liveConf && persistedConf) {
          return {
            ...liveConf,
            ...persistedConf,
            toolPublicId: liveConf.toolPublicId || persistedConf.toolPublicId,
            messageId: liveConf.messageId || persistedConf.messageId,
            status: persistedConf.status !== 'pending' ? persistedConf.status : liveConf.status,
          };
        }
        return liveConf || persistedConf;
      })(),
      conversationTitle: liveMessage.conversationTitle || persistedMessage.conversationTitle,
    });
  }

  return enrichedReversed.reverse();
};

const stripStreamProgressLines = (value: string | null | undefined) => {
  if (!value) return '';

  return value
    .split(/\r?\n/)
    .filter((line) => !line.trim().startsWith(STREAM_PROGRESS_PREFIX))
    .join('\n')
    .trim();
};

const hasAgentAnswerContent = (message: ChatMessage) =>
  parseAgentResponse(message.content).content.trim().length > 0 ||
  Boolean(message.pendingImage) ||
  Boolean(message.attachments?.length) ||
  Boolean(message.diagrams?.length) ||
  Boolean(message.confirmation);

const hasAgentNonProgressReasoning = (message: ChatMessage) =>
  stripStreamProgressLines(message.reasoning).length > 0;

const isProgressOnlyAgentMessage = (message: ChatMessage) =>
  message.role === 'agent' &&
  !hasAgentAnswerContent(message) &&
  !hasAgentNonProgressReasoning(message) &&
  Boolean(message.reasoning?.trim());

export const hasRenderableMessageContent = (message: ChatMessage) => {
  if (message.role === 'user') {
    return message.content.trim().length > 0 || Boolean(message.attachments?.length);
  }

  return (
    hasAgentAnswerContent(message) ||
    hasAgentNonProgressReasoning(message) ||
    Boolean(message.reasoning?.trim()) ||
    Boolean(message.diagrams?.length) ||
    Boolean(message.confirmation)
  );
};

const MESSAGE_ROLE_ORDER: Record<ChatMessage['role'], number> = {
  user: 0,
  agent: 1,
};

const LIVE_TURN_TIMESTAMP_TOLERANCE_MS = 1500;

export const orderMessagesForDisplay = (messages: ChatMessage[]) =>
  messages
    .map((message, index) => ({ message, index }))
    .sort((left, right) => {
      const leftTime = left.message.timestamp.getTime();
      const rightTime = right.message.timestamp.getTime();
      const timeDiff = leftTime - rightTime;

      if (
        Number.isFinite(leftTime) &&
        Number.isFinite(rightTime) &&
        Math.abs(timeDiff) > LIVE_TURN_TIMESTAMP_TOLERANCE_MS
      ) {
        return timeDiff;
      }

      if (left.message.role !== right.message.role) {
        return MESSAGE_ROLE_ORDER[left.message.role] - MESSAGE_ROLE_ORDER[right.message.role];
      }

      return left.index - right.index;
    })
    .map(({ message }) => message);

export const shouldHideStaleProgressOnlyAgentMessage = (
  message: ChatMessage,
  index: number,
  messages: ChatMessage[]
) => {
  if (!isProgressOnlyAgentMessage(message)) {
    return false;
  }

  const previousUserIndex = messages
    .slice(0, index)
    .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
    .reverse()
    .find(({ candidate }) => candidate.role === 'user')?.candidateIndex;
  const nextUserIndex = messages.findIndex(
    (candidate, candidateIndex) => candidateIndex > index && candidate.role === 'user'
  );
  const turnStartIndex = previousUserIndex !== undefined ? previousUserIndex + 1 : 0;
  const turnEndIndex = nextUserIndex >= 0 ? nextUserIndex : messages.length;

  return messages
    .slice(turnStartIndex, turnEndIndex)
    .some((candidate) => candidate.role === 'agent' && hasAgentAnswerContent(candidate));
};
